import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Plus, Cloud } from 'lucide-react';
import { apiService, Connector } from '../services/api';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { parseEnumParam, parseFlagParam } from '../navigation/urlCodec';
import ConnectorCard from './ConnectorCard';
import ConnectorDetailSidebar from './ConnectorDetailSidebar';
import CustomSelect from './CustomSelect';
import { useTabActivity } from '../contexts/TabActivityContext';
import './CloudConnectorsTab.css';

const OWNED_KEYS = ['connector', 'new', 'q', 'status', 'provider', 'sort', 'dir'];

const CloudConnectorsTab: React.FC = () => {
  // Connectors state
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [tempConnector, setTempConnector] = useState<Connector | null>(null);

  // Layout state
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'status'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterProvider, setFilterProvider] = useState<string>('');
  const [urlNotice, setUrlNotice] = useState<string | null>(null);
  const [urlHydrated, setUrlHydrated] = useState(false);
  const historyModeRef = React.useRef<HistoryMode>('replace');
  const isApplyingUrlRef = React.useRef(false);
  const pendingConnectorIdRef = React.useRef<string | null | undefined>(undefined);
  const pendingCreateRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useTabActivity();
  const isActiveRef = React.useRef(isActive);
  isActiveRef.current = isActive;

  // Sidebar width
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('connectorSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });

  // Listen for sidebar width changes
  useEffect(() => {
    const handleSidebarWidthChange = (event: CustomEvent) => {
      setSidebarWidth(event.detail.width);
    };

    window.addEventListener('connectorSidebarWidthChange' as any, handleSidebarWidthChange);

    return () => {
      window.removeEventListener('connectorSidebarWidthChange' as any, handleSidebarWidthChange);
    };
  }, []);

  // Load data on mount
  useEffect(() => {
    loadConnectors();
  }, []);

  useEffect(() => {
    isApplyingUrlRef.current = true;
    const params = new URLSearchParams(location.search);
    setSearchTerm(params.get('q') || '');
    setFilterStatus(params.get('status') || '');
    setFilterProvider(params.get('provider') || '');
    setSortBy(parseEnumParam(params.get('sort'), ['name', 'created_at', 'status'] as const, 'created_at'));
    setSortOrder(parseEnumParam(params.get('dir'), ['asc', 'desc'] as const, 'desc'));
    pendingConnectorIdRef.current = params.get('connector');
    pendingCreateRef.current = parseFlagParam(params.get('new'));

    const timer = window.setTimeout(() => {
      isApplyingUrlRef.current = false;
      setUrlHydrated(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.search]);

  useEffect(() => {
    if (!urlHydrated || (pendingConnectorIdRef.current === undefined && !pendingCreateRef.current)) return;

    const applyPendingSelection = async () => {
      if (pendingCreateRef.current) {
        pendingCreateRef.current = false;
        pendingConnectorIdRef.current = undefined;
        handleCreateConnector();
        return;
      }

      const requestedId = pendingConnectorIdRef.current;
      if (!requestedId) {
        pendingConnectorIdRef.current = undefined;
        setShowSidebar(false);
        setSelectedConnector(null);
        setIsCreating(false);
        setTempConnector(null);
        return;
      }

      const localMatch = connectors.find((connector) => connector.connector_id === requestedId);
      if (localMatch) {
        setSelectedConnector(localMatch);
        setShowSidebar(true);
        setIsCreating(false);
        setTempConnector(null);
        pendingConnectorIdRef.current = undefined;
        return;
      }

      try {
        const fetched = await apiService.getConnector(requestedId);
        setConnectors((prev) => (prev.some((item) => item.connector_id === fetched.connector_id) ? prev : [fetched, ...prev]));
        setSelectedConnector(fetched);
        setShowSidebar(true);
        setIsCreating(false);
        setTempConnector(null);
      } catch {
        setUrlNotice('Requested connector no longer exists. Showing connector list.');
        setShowSidebar(false);
        setSelectedConnector(null);
        historyModeRef.current = 'replace';
        if (isActiveRef.current) commitOwnedSearch({
          location,
          navigate,
          ownedKeys: OWNED_KEYS,
          updates: { connector: null, new: null },
          mode: 'replace',
        });
      } finally {
        pendingConnectorIdRef.current = undefined;
      }
    };

    applyPendingSelection();
  }, [connectors, location, navigate, urlHydrated]);

  useEffect(() => {
    if (!isActive || !urlHydrated || isApplyingUrlRef.current || !!pendingConnectorIdRef.current || pendingCreateRef.current) return;

    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';

    const isTempSelection = !!selectedConnector?.connector_id?.startsWith('temp_');
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        q: searchTerm || null,
        status: filterStatus || null,
        provider: filterProvider || null,
        sort: sortBy,
        dir: sortOrder,
        connector: showSidebar && selectedConnector && !isCreating && !isTempSelection ? selectedConnector.connector_id : null,
        new: showSidebar && isCreating ? 1 : null,
      },
      mode,
    });
  }, [
    filterProvider,
    isActive,
    filterStatus,
    isCreating,
    location,
    navigate,
    searchTerm,
    selectedConnector,
    showSidebar,
    sortBy,
    sortOrder,
    urlHydrated,
  ]);

  // Load connectors
  const loadConnectors = async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getConnectors();
      setConnectors(data);
    } catch (error) {
      console.error('Failed to load connectors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle connector click
  const handleConnectorClick = (connector: Connector) => {
    historyModeRef.current = 'push';
    if (tempConnector && connector.connector_id === tempConnector.connector_id) {
      // Clicking on temp connector while creating
      setShowSidebar(true);
      return;
    }

    // Clear temp connector if clicking on a real connector
    if (tempConnector) {
      setTempConnector(null);
      setIsCreating(false);
    }

    setSelectedConnector(connector);
    setShowSidebar(true);
  };

  // Handle create new connector
  const handleCreateConnector = () => {
    historyModeRef.current = 'push';
    const newTempConnector: Connector = {
      connector_id: `temp_${Date.now()}`,
      name: '',
      provider: 'gcs',
      bucket: '',
      scopes: ['import'],
      export_base_path: '',
      gcp_project_id: '',
      region: '',
      storage_account: '',
      container: '',
      status: 'disabled',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_tested_at: undefined,
      last_test_error: undefined
    };

    setTempConnector(newTempConnector);
    setSelectedConnector(newTempConnector);
    setIsCreating(true);
    setShowSidebar(true);
  };

  // Handle close sidebar
  const handleCloseSidebar = () => {
    historyModeRef.current = 'push';
    setShowSidebar(false);
    setIsCreating(false);

    // Clear temp connector when closing
    if (tempConnector) {
      setTempConnector(null);
      setSelectedConnector(null);
    }
  };

  // Handle save connector
  const handleSaveConnector = (savedConnector: Connector) => {
    if (isCreating) {
      // Add new connector to list
      setConnectors(prev => [savedConnector, ...prev]);
      setTempConnector(null);
      setIsCreating(false);
      setSelectedConnector(savedConnector);
    } else {
      // Update existing connector in list
      setConnectors(prev =>
        prev.map(c => c.connector_id === savedConnector.connector_id ? savedConnector : c)
      );
      setSelectedConnector(savedConnector);
    }
  };

  // Handle delete connector
  const handleDeleteConnector = (connectorId: string) => {
    historyModeRef.current = 'push';
    setConnectors(prev => prev.filter(c => c.connector_id !== connectorId));
    setSelectedConnector(null);
  };

  // Filter and sort connectors
  const filteredConnectors = useMemo(() => {
    let filtered = [...connectors];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(term) ||
        c.bucket?.toLowerCase().includes(term) ||
        c.provider.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (filterStatus) {
      filtered = filtered.filter(c => c.status === filterStatus);
    }

    // Apply provider filter
    if (filterProvider) {
      filtered = filtered.filter(c => c.provider === filterProvider);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [connectors, searchTerm, filterStatus, filterProvider, sortBy, sortOrder]);

  // All connectors to display (including temp)
  const displayConnectors = useMemo(() => {
    if (tempConnector) {
      return [tempConnector, ...filteredConnectors];
    }
    return filteredConnectors;
  }, [tempConnector, filteredConnectors]);

  // Calculate container margin based on sidebar
  const containerStyle = useMemo(() => {
    if (showSidebar) {
      return { marginRight: `${sidebarWidth}px` };
    }
    return {};
  }, [showSidebar, sidebarWidth]);

  return (
    <div className="connectors-tab">
      <div
        className="connectors-container"
        style={containerStyle}
      >
        {urlNotice && (
          <div className="error-message">
            {urlNotice}
            <button onClick={() => setUrlNotice(null)}>×</button>
          </div>
        )}
        {/* Section Header */}
        <div className="connectors-section">
          <div className="section-header">
            <div className="section-title">
              <Cloud size={20} />
              <h2>Cloud Connectors</h2>
              <span className="connector-count">{connectors.length} connectors</span>
            </div>

            <div className="section-controls">
              {/* Search */}
              <div className="search-input-wrapper">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search connectors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Provider Filter */}
              <CustomSelect
                value={filterProvider}
                onChange={setFilterProvider}
                options={[
                  { value: '', label: 'All Providers' },
                  { value: 'gcs', label: 'Google Cloud Storage' },
                  { value: 's3', label: 'Amazon S3' },
                  { value: 'azure', label: 'Azure Blob Storage' }
                ]}
              />

              {/* Status Filter */}
              <CustomSelect
                value={filterStatus}
                onChange={setFilterStatus}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'active', label: 'Active' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'disabled', label: 'Disabled' }
                ]}
              />

              {/* Sort */}
              <div className="sort-controls-custom">
                <CustomSelect
                  value={sortBy}
                  onChange={(value) => setSortBy(value as any)}
                  className="sort-dropdown"
                  options={[
                    { value: 'created_at', label: 'Date Created' },
                    { value: 'name', label: 'Name' },
                    { value: 'status', label: 'Status' }
                  ]}
                />
                <button
                  className="sort-order-btn"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>

              {/* Refresh */}
              <button
                className="refresh-btn"
                onClick={loadConnectors}
                disabled={isLoading}
                title="Refresh connectors"
              >
                <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
              </button>

              {/* New Connector */}
              <button
                className="new-connector-btn"
                onClick={handleCreateConnector}
                disabled={isCreating}
              >
                <Plus size={16} />
                New Connector
              </button>
            </div>
          </div>

          {/* Connectors Grid */}
          <div className="connectors-grid">
            {isLoading && connectors.length === 0 ? (
              <div className="loading-state">
                <RefreshCw size={24} className="spinning" />
                <span>Loading connectors...</span>
              </div>
            ) : displayConnectors.length === 0 ? (
              <div className="empty-state">
                <Cloud size={48} />
                <h3>No Connectors</h3>
                <p>Create your first cloud connector to import videos from cloud storage.</p>
                <button className="create-first-btn" onClick={handleCreateConnector}>
                  <Plus size={16} />
                  Create Connector
                </button>
              </div>
            ) : (
              displayConnectors.map(connector => (
                <ConnectorCard
                  key={connector.connector_id}
                  connector={connector}
                  isSelected={selectedConnector?.connector_id === connector.connector_id}
                  onClick={() => handleConnectorClick(connector)}
                  isCreating={connector.connector_id === tempConnector?.connector_id}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Detail Sidebar */}
      <ConnectorDetailSidebar
        connector={selectedConnector}
        isOpen={showSidebar}
        onClose={handleCloseSidebar}
        onSave={handleSaveConnector}
        onDelete={handleDeleteConnector}
        isCreating={isCreating}
      />
    </div>
  );
};

export default CloudConnectorsTab;
